import type { Ref } from 'vue';
import type { Router } from 'vue-router';
import type { BookingStop } from '@/models/advancedJobBooking';

import { isCancel } from 'axios';

import {
  fetchPostAddressBookUserAddressbook,
  fetchPostJobAttachment,
} from '@/helpers/fetchers';
import { showSuccessNotification } from '@/helpers/notifiers';
import { sendError } from '@/helpers/sentry';
import { KJobBookingError } from '@/helpers/throwables';
import * as routeNames from '@/constants/routeNames';

/**
 * What to do after a successful booking:
 * - `navigate` — open the booked job (default, the main "Book job" action);
 * - `reset` — clear the form for a fresh booking ("Book & Create New Job");
 * - `keep` — leave the form filled to book a duplicate ("Book & Duplicate").
 */
export type AfterBook = 'navigate' | 'reset' | 'keep';

export interface JobBookingSubmitOptions {
  stops: Ref<BookingStop[]>;
  attachments: Ref<File[]>;
  isSubmitting: Ref<boolean>;
  bookingErrors: Ref<string[]>;
  reset: () => void;
  router: Router;
  postBooking: (
    signal: AbortSignal,
  ) => Promise<{ jobId: number; jobDate: string }>;
}

export interface JobBookingSubmitComposable {
  submit: (after?: AfterBook) => Promise<boolean>;
}

async function uploadAttachmentsForJob(
  attachments: File[],
  rhId: number,
  rhDate: string,
): Promise<void> {
  if (!attachments.length) return;
  await fetchPostJobAttachment({
    rh_id: rhId,
    rh_date: rhDate,
    files: attachments,
  }).catch(() => undefined);
}

async function saveStopsToAddressBook(stops: BookingStop[]): Promise<void> {
  const tasks = stops
    .filter(
      (stop): stop is BookingStop & { suburb_id: number } =>
        stop.save_to_address_book && stop.suburb_id !== null,
    )
    .map((stop) =>
      fetchPostAddressBookUserAddressbook({
        suburb: stop.suburb_id,
        name: stop.name,
        line1: stop.address_line1,
        line2: stop.address_line2,
        line3: '',
        city: '',
        note_name: stop.note_name,
        note_phone: stop.note_phone,
        note_email: stop.note_email,
        note_text: stop.note_text,
        warehouse: stop.warehouse,
      }).catch(() => undefined),
    );
  await Promise.all(tasks);
}

export default function useJobBookingSubmit(
  options: JobBookingSubmitOptions,
): JobBookingSubmitComposable {
  const {
    stops,
    attachments,
    isSubmitting,
    bookingErrors,
    reset,
    router,
    postBooking,
  } = options;
  let submitController: AbortController | undefined;

  async function submit(after: AfterBook = 'navigate'): Promise<boolean> {
    if (submitController) submitController.abort();
    submitController = new AbortController();
    isSubmitting.value = true;
    bookingErrors.value = [];
    try {
      const { jobId, jobDate } = await postBooking(submitController.signal);
      await Promise.all([
        uploadAttachmentsForJob(attachments.value, jobId, jobDate),
        saveStopsToAddressBook(stops.value),
      ]);
      showSuccessNotification({
        title: 'Job created',
        text: `#${jobId}, ${jobDate}`,
      });
      if (after === 'navigate') {
        await router.push({
          name: routeNames.DETAILED_QUERY_JOB,
          query: { job_id: String(jobId), job_date: jobDate },
        });
      } else if (after === 'reset') {
        reset();
      }
      // `keep`: form left untouched for a duplicate booking.
      return true;
    } catch (err) {
      if (isCancel(err)) return false;
      if (err instanceof KJobBookingError) {
        bookingErrors.value = err.reasons.length
          ? err.reasons
          : ['Something went wrong. Please try again.'];
        return false;
      }
      // Unexpected (non-API) failure — report it for debugging, but keep the
      // flow from surfacing as an unhandled rejection in the caller.
      sendError(err, 'useJobBookingSubmit.submit');
      bookingErrors.value = ['Something went wrong. Please try again.'];
      return false;
    } finally {
      isSubmitting.value = false;
    }
  }

  return { submit };
}
